import { navigateToApp } from '../../auth/appNavigation';
import { appUrl } from '../../config/appUrl';
import { apiRequest } from '../../api/http';
import { ArrowLeft, Flame, History, Search } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { track } from './types';

// Same history key and size as the app (SearchDiscoveryRepository).
const HISTORY_KEY = 'beaw.search.history.v1.guest';

function readHistory(): string[] {
  try { const value = JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]'); return Array.isArray(value) ? value.filter(item => typeof item === 'string').slice(0, 5) : []; } catch { return []; }
}

function remember(query: string) {
  const next = [query, ...readHistory().filter(item => item.toLocaleLowerCase() !== query.toLocaleLowerCase())].slice(0, 5);
  try { localStorage.setItem(HISTORY_KEY, JSON.stringify(next)); } catch { /* History must not block a search. */ }
}

// Trending changes slowly; reuse it for a few minutes within the visit.
const trendingCache = new Map<string, { at: number; items: string[] }>();
const TRENDING_TTL_MS = 5 * 60_000;

async function loadTrending(city: string): Promise<string[]> {
  const hit = trendingCache.get(city);
  if (hit && Date.now() - hit.at < TRENDING_TTL_MS) return hit.items;
  const items = await fetchTrending(city);
  if (items.length) trendingCache.set(city, { at: Date.now(), items });
  return items;
}

async function fetchTrending(city: string): Promise<string[]> {
  for (let attempt = 0; attempt < 2; attempt++) {
    const data = await apiRequest<{ items?: Array<{ query?: string }>; refreshing?: boolean }>('/search/trending', { query: { city } });
    const items = (data.items || []).map(item => String(item.query || '').trim()).filter(Boolean);
    if (items.length || !data.refreshing || attempt) return items.slice(0, 8);
    await new Promise(resolve => setTimeout(resolve, 1000));
  }
  return [];
}

/**
 * The app home's search behaviour (BeawDiscoveryFilterRail): the magnifier
 * swaps the pill rail for an inline search field, with recent and trending
 * searches underneath; submitting opens the app's search results.
 */
export function RailSearch({ city, locale, onClose }: { city: string; locale: 'th' | 'en'; onClose: () => void }) {
  const [query, setQuery] = useState('');
  const [history] = useState(readHistory);
  const [trending, setTrending] = useState<string[] | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const english = locale === 'en';
  useEffect(() => { input.current?.focus(); }, []);
  useEffect(() => {
    let alive = true;
    loadTrending(city).then(items => { if (alive) setTrending(items); }).catch(() => { if (alive) setTrending([]); });
    return () => { alive = false; };
  }, [city]);
  const submit = (value: string) => {
    const clean = value.trim();
    if (!clean) { input.current?.focus(); return; }
    remember(clean);
    track('landing_search_submit', { city, query: clean });
    void navigateToApp(appUrl(`/search/results?${new URLSearchParams({ q: clean, city: city || 'Chiang Mai', lang: locale })}`));
  };

  return <div className="hx-search">
    <form className="hx-search__field" role="search" onSubmit={event => { event.preventDefault(); submit(query); }}>
      <button type="button" className="hx-search__icon" aria-label={english ? 'Back' : 'ย้อนกลับ'} onClick={onClose}><ArrowLeft /></button>
      <input ref={input} type="search" enterKeyHint="search" value={query} onChange={event => setQuery(event.target.value)} onKeyDown={event => { if (event.key === 'Escape') onClose(); }} placeholder={english ? 'Search for cafes, matcha, vibes...' : 'ค้นหาคาเฟ่ มัทฉะ หรือบรรยากาศ...'} aria-label={english ? 'Search' : 'ค้นหา'} />
      <button type="submit" className="hx-search__icon" aria-label={english ? 'Search' : 'ค้นหา'}><Search /></button>
    </form>
    <div className="hx-search__panel">
      <p className="hx-search__heading"><History />{english ? 'Recent searches' : 'ประวัติการค้นหา'}</p>
      {history.length ? history.map(item => <button type="button" key={`h-${item}`} className="hx-search__row" onClick={() => submit(item)}><History />{item}</button>)
        : <p className="hx-search__empty">{english ? 'No recent searches' : 'ยังไม่มีประวัติการค้นหา'}</p>}
      <p className="hx-search__heading"><Flame />{english ? 'Trending searches' : 'คำค้นยอดนิยม'}</p>
      {trending === null ? <span className="hx-search__loading" aria-label={english ? 'Loading' : 'กำลังโหลด'} />
        : trending.length ? trending.map(item => <button type="button" key={`t-${item}`} className="hx-search__row" onClick={() => submit(item)}><Search />{item}</button>)
        : <p className="hx-search__empty">{english ? 'No trending searches yet' : 'ยังไม่มีคำค้นยอดนิยม'}</p>}
    </div>
  </div>;
}
